// Ajustes do Agzos Office editáveis pelo CMS (/cms.html): servidor, contas de CLI, campus
// (prédios, salas), personagens e tema.
//
// Gravados em <stateDir>/settings.json. As variáveis AGZOS_* são só o PADRÃO inicial: o que o
// CMS salvar vence. Puro (sem disco nem rede): validação e merge testáveis em Node.

import { CAST_BY_ID } from './cast.js';
import { lookErrors } from './look-edit.js';
import type { Look } from './look.js';
import type { Provider } from './types.js';

export const SETTINGS_VERSION = 1;

export const CLI_KINDS: readonly Provider[] = ['mcode', 'claude', 'codex', 'opencode', 'antigravity'];

/** Temas de decoração (os mesmos ids de src/web/art/seasonal.ts) + 'auto' = pela data. */
export const SEASON_CHOICES = ['auto', 'carnaval', 'pascoa', 'aniversario', 'junina', 'halloween', 'natal', 'padrao'] as const;
export type SeasonChoice = (typeof SEASON_CHOICES)[number];

/** Áreas comuns da sede (ids das plantas em src/web/maps). */
export const COMMON_AREAS = ['recepcao', 'copa', 'lounge', 'banheiros', 'servidores', 'reuniao', 'terraco'] as const;
export type CommonArea = (typeof COMMON_AREAS)[number];

/** Uma conta de CLI observada (dá para ter várias do mesmo tipo). */
export interface CliAccount {
  /** Id estável da conta (a-z, 0-9, '-'): 'claude', 'claude-conta2'. */
  id: string;
  kind: Provider;
  /** Pasta de dados da CLI (ex.: /root/.claude). */
  dataDir: string;
  enabled: boolean;
  /** Nome de exibição (ex.: 'Claude (conta 2)'). */
  label?: string;
  /** Sigla curta no crachá (ex.: 'C2'), até 3 letras. */
  tag?: string;
  /** Cor do crachá (#rrggbb). */
  color?: string;
}

/** Regra "projeto → prédio": `match` é prefixo do nome do projeto ou glob com '*' ('agzos-*'). */
export interface BuildingRule {
  match: string;
  building: string;
}

export interface BuildingSettings {
  name?: string;
  /** Ordem na rua (menor primeiro). */
  order?: number;
}

/** Ajustes de uma sala de projeto (chave: nome do projeto). */
export interface RoomSettings {
  name?: string;
  /** Prédio (vence as regras). */
  building?: string;
  /** Andar preferido (0 = térreo). */
  floor?: number;
  /** Mesas mínimas. */
  desks?: number;
  hidden?: boolean;
}

/** Personagem e nome de um agente. Chave: id do agente ('claude:<sessão>') ou 'project:<nome>' (o principal do projeto). */
export interface PersonaSettings {
  castId?: string;
  name?: string;
  /** Aparência por peças (editor de personagem). Vale sobre o castId: sem ele, é o personagem do elenco. */
  look?: Look;
}

export interface CampusSettings {
  /** Prédio que é a sede (tem todas as áreas comuns). Vazio = 'agzos' ou o maior. */
  hq?: string;
  buildings: Record<string, BuildingSettings>;
  rules: BuildingRule[];
  rooms: Record<string, RoomSettings>;
  /** Áreas comuns ligadas na sede. */
  commons: Record<CommonArea, boolean>;
}

export interface ThemeSettings {
  season: SeasonChoice;
  night: 'auto' | 'on' | 'off';
  lang: 'auto' | 'pt' | 'en' | 'es';
  /** Título do escritório (topo e aba do navegador). */
  title: string;
  /** Balões de fala com a atividade acima dos personagens (padrão: ligados). */
  balloons?: boolean;
  /** Cartão de detalhes ao passar o mouse num personagem (padrão: ligado). */
  hoverCard?: boolean;
  /** Telas dos computadores: animadas, quadro fixo ou apagadas (padrão: animadas). */
  screens?: 'anim' | 'still' | 'off';
  /** Terminal ao vivo: de quanto em quanto tempo a conversa é relida (s, 1 a 30; padrão 2). */
  liveRefreshS?: number;
}

export interface ServerSettings {
  /** Intervalo de leitura dos adapters (ms). */
  pollMs: number;
  /** Validade da sessão de login (s). */
  sessionTtlS: number;
  logLevel: 'debug' | 'info' | 'warn' | 'error';
  /** Subagente parado sai do escritório depois deste tempo (min); 0 = na hora. Padrão 5. */
  subagentLingerMin?: number;
  /** Notificações no celular (B29-M5): contato do VAPID e se o aviso de "concluiu" sai do servidor. */
  push?: PushSettings;
  /** Terminal interativo (6.1): pseudo-terminal no navegador. Desligado por padrão; exige admin com TOTP. */
  terminalInterativo?: boolean;
}

export interface PushSettings {
  /** E-mail de contato do VAPID (vai como mailto:). Sem ele, vai a URL pública do escritório. */
  contato?: string;
  /** Avisar quando um agente conclui um turno (padrão desligado: só "precisa de você"). */
  concluiu?: boolean;
}

/** Padrão do tempo que um subagente que terminou ainda fica no escritório. */
export const SUBAGENT_LINGER_MIN = 5;

export interface OfficeSettings {
  version: number;
  server: ServerSettings;
  clis: CliAccount[];
  campus: CampusSettings;
  personas: Record<string, PersonaSettings>;
  theme: ThemeSettings;
}

/** O que vai para o navegador no snapshot (nada de pasta nem config do servidor). */
export interface PublicSettings {
  campus: CampusSettings;
  theme: ThemeSettings;
  clis: Array<Pick<CliAccount, 'id' | 'kind' | 'label' | 'tag' | 'color' | 'enabled'>>;
}

export interface SettingsDefaults {
  pollMs: number;
  sessionTtlS: number;
  logLevel: ServerSettings['logLevel'];
  /** Pasta de dados padrão de cada CLI. */
  dirs: Record<Provider, string>;
  /** CLIs ligadas no padrão. */
  enabled: readonly Provider[];
}

const DEFAULT_LABELS: Record<Provider, string> = {
  mcode: 'MiniMax Code',
  claude: 'Claude Code',
  codex: 'Codex',
  opencode: 'OpenCode',
  antigravity: 'Antigravity',
};

export function defaultSettings(d: SettingsDefaults): OfficeSettings {
  return {
    version: SETTINGS_VERSION,
    server: { pollMs: d.pollMs, sessionTtlS: d.sessionTtlS, logLevel: d.logLevel, subagentLingerMin: SUBAGENT_LINGER_MIN },
    clis: CLI_KINDS.map((kind) => ({ id: kind, kind, dataDir: d.dirs[kind], enabled: d.enabled.includes(kind), label: DEFAULT_LABELS[kind] })),
    campus: {
      buildings: {},
      rules: [],
      rooms: {},
      commons: Object.fromEntries(COMMON_AREAS.map((a) => [a, true])) as Record<CommonArea, boolean>,
    },
    personas: {},
    theme: { season: 'auto', night: 'auto', lang: 'auto', title: 'Agzos Office' },
  };
}

export function publicSettings(s: OfficeSettings): PublicSettings {
  return {
    campus: s.campus,
    theme: s.theme,
    clis: s.clis.map(({ id, kind, label, tag, color, enabled }) => ({ id, kind, enabled, ...(label ? { label } : {}), ...(tag ? { tag } : {}), ...(color ? { color } : {}) })),
  };
}

// ------------------------------------------------------------------ validação

const ID_RE = /^[a-z0-9][a-z0-9-]{0,31}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const COLOR_RE = /^#[0-9a-fA-F]{6}$/;

/** Erros de um conjunto de ajustes (vazio = ok). Mensagens em português, prontas para a UI. */
export function validateSettings(s: OfficeSettings): string[] {
  const errors: string[] = [];
  const { server, clis, campus, personas, theme } = s;
  if (!Number.isInteger(server.pollMs) || server.pollMs < 500 || server.pollMs > 60_000) errors.push('servidor: intervalo de leitura entre 500 e 60000 ms');
  if (!Number.isInteger(server.sessionTtlS) || server.sessionTtlS < 300 || server.sessionTtlS > 30 * 86_400) errors.push('servidor: validade do login entre 5 min e 30 dias');
  if (!['debug', 'info', 'warn', 'error'].includes(server.logLevel)) errors.push('servidor: nível de log inválido');
  if (theme.screens !== undefined && !['anim', 'still', 'off'].includes(theme.screens)) errors.push('tema: telas dos computadores inválidas');
  if (theme.liveRefreshS !== undefined && (!Number.isInteger(theme.liveRefreshS) || theme.liveRefreshS < 1 || theme.liveRefreshS > 30)) errors.push('tema: atualização do terminal ao vivo entre 1 e 30 s');
  if (server.subagentLingerMin !== undefined && (!Number.isInteger(server.subagentLingerMin) || server.subagentLingerMin < 0 || server.subagentLingerMin > 240)) {
    errors.push('servidor: tempo dos subagentes parados entre 0 e 240 min');
  }
  if (server.terminalInterativo !== undefined && typeof server.terminalInterativo !== 'boolean') errors.push('servidor: terminal interativo precisa ser sim ou não');
  const contato = server.push?.contato;
  if (contato !== undefined && contato !== '' && (typeof contato !== 'string' || contato.length > 120 || !EMAIL_RE.test(contato))) {
    errors.push('notificações: contato precisa ser um e-mail (até 120 caracteres)');
  }
  if (server.push?.concluiu !== undefined && typeof server.push.concluiu !== 'boolean') errors.push('notificações: concluiu precisa ser sim ou não');

  const ids = new Set<string>();
  for (const c of clis) {
    const where = `CLI ${c.id || '(sem id)'}`;
    if (!ID_RE.test(c.id)) errors.push(`${where}: id só com a-z, 0-9 e '-' (até 32)`);
    if (ids.has(c.id)) errors.push(`${where}: id repetido`);
    ids.add(c.id);
    if (!CLI_KINDS.includes(c.kind)) errors.push(`${where}: tipo desconhecido`);
    if (!c.dataDir.startsWith('/') || c.dataDir.includes('..')) errors.push(`${where}: pasta precisa ser um caminho absoluto, sem '..'`);
    if (c.tag !== undefined && !/^[A-Z0-9]{1,3}$/.test(c.tag)) errors.push(`${where}: sigla com 1 a 3 letras/números maiúsculos`);
    if (c.color !== undefined && !COLOR_RE.test(c.color)) errors.push(`${where}: cor no formato #rrggbb`);
    if (c.label !== undefined && c.label.length > 40) errors.push(`${where}: rótulo até 40 caracteres`);
  }

  for (const [key, b] of Object.entries(campus.buildings)) {
    if (!ID_RE.test(key)) errors.push(`prédio ${key}: chave inválida`);
    if (b.name !== undefined && (b.name.length < 1 || b.name.length > 40)) errors.push(`prédio ${key}: nome de 1 a 40 caracteres`);
    if (b.order !== undefined && !Number.isInteger(b.order)) errors.push(`prédio ${key}: ordem precisa ser inteira`);
  }
  if (campus.hq !== undefined && campus.hq !== '' && !ID_RE.test(campus.hq)) errors.push('sede: chave de prédio inválida');
  for (const r of campus.rules) {
    if (!r.match || r.match.length > 60) errors.push('regra: padrão de 1 a 60 caracteres');
    if (!ID_RE.test(r.building)) errors.push(`regra ${r.match}: prédio inválido`);
  }
  for (const [project, r] of Object.entries(campus.rooms)) {
    if (r.name !== undefined && (r.name.length < 1 || r.name.length > 40)) errors.push(`sala ${project}: nome de 1 a 40 caracteres`);
    if (r.building !== undefined && !ID_RE.test(r.building)) errors.push(`sala ${project}: prédio inválido`);
    if (r.floor !== undefined && (!Number.isInteger(r.floor) || r.floor < 0 || r.floor > 20)) errors.push(`sala ${project}: andar de 0 a 20`);
    if (r.desks !== undefined && (!Number.isInteger(r.desks) || r.desks < 1 || r.desks > 12)) errors.push(`sala ${project}: mesas de 1 a 12`);
  }

  for (const [key, p] of Object.entries(personas)) {
    if (!key.startsWith('project:') && !key.includes(':')) errors.push(`personagem ${key}: chave precisa ser 'project:<nome>' ou o id do agente`);
    if (p.castId !== undefined && !CAST_BY_ID.has(p.castId)) errors.push(`personagem ${key}: personagem ${p.castId} não existe`);
    if (p.name !== undefined && (p.name.length < 1 || p.name.length > 24)) errors.push(`personagem ${key}: nome de 1 a 24 caracteres`);
    if (p.look !== undefined) errors.push(...lookErrors(p.look, `personagem ${key}`));
  }

  if (!SEASON_CHOICES.includes(theme.season)) errors.push('tema: estação inválida');
  if (!['auto', 'on', 'off'].includes(theme.night)) errors.push('tema: noite inválida');
  if (!['auto', 'pt', 'en', 'es'].includes(theme.lang)) errors.push('tema: idioma inválido');
  if (!theme.title || theme.title.length > 40) errors.push('tema: título de 1 a 40 caracteres');
  return errors;
}

// ------------------------------------------------------------------ merge

/** Ajuste parcial vindo do CMS: objetos se fundem; listas e mapas inteiros substituem. */
export interface SettingsPatch {
  server?: Partial<ServerSettings>;
  clis?: CliAccount[];
  campus?: Partial<CampusSettings>;
  personas?: Record<string, PersonaSettings | null>;
  theme?: Partial<ThemeSettings>;
}

/** Aplica um ajuste parcial. Em `personas`, valor null apaga a chave. */
export function mergeSettings(current: OfficeSettings, patch: SettingsPatch): OfficeSettings {
  const personas = { ...current.personas };
  for (const [k, v] of Object.entries(patch.personas ?? {})) {
    if (v === null) delete personas[k];
    else personas[k] = v;
  }
  return {
    version: SETTINGS_VERSION,
    server: { ...current.server, ...(patch.server ?? {}) },
    clis: patch.clis ?? current.clis,
    campus: { ...current.campus, ...(patch.campus ?? {}), commons: { ...current.campus.commons, ...(patch.campus?.commons ?? {}) } },
    personas,
    theme: { ...current.theme, ...(patch.theme ?? {}) },
  };
}

/**
 * Lê um settings.json (ou import) sobre os padrões: chaves que faltam vêm dos padrões, chaves
 * desconhecidas são ignoradas. Não valida (quem chama valida).
 */
export function normalizeSettings(raw: unknown, defaults: OfficeSettings): OfficeSettings {
  if (!raw || typeof raw !== 'object') return defaults;
  const r = raw as Partial<OfficeSettings>;
  return mergeSettings(defaults, {
    ...(r.server ? { server: r.server } : {}),
    ...(Array.isArray(r.clis) ? { clis: r.clis } : {}),
    ...(r.campus ? { campus: { ...defaults.campus, ...r.campus, buildings: r.campus.buildings ?? {}, rules: r.campus.rules ?? [], rooms: r.campus.rooms ?? {} } } : {}),
    ...(r.personas ? { personas: r.personas } : {}),
    ...(r.theme ? { theme: r.theme } : {}),
  });
}

/** Prédio de um projeto pelas regras ('agzos-*' glob, ou prefixo). undefined = nenhuma regra. */
export function buildingByRules(project: string, rules: readonly BuildingRule[]): string | undefined {
  for (const r of rules) {
    if (r.match.includes('*')) {
      const re = new RegExp(`^${r.match.split('*').map((p) => p.replace(/[.+?^${}()|[\]\\]/g, '\\$&')).join('.*')}$`, 'i');
      if (re.test(project)) return r.building;
    } else if (project.toLowerCase().startsWith(r.match.toLowerCase())) return r.building;
  }
  return undefined;
}
